/* @flow */
import * as React from 'react';

import CheckboxList from 'components/CheckboxList.jsx';
import Checkbox from 'components/Checkbox.jsx';
import Field from 'components/Field.jsx';
import Heading from 'components/Heading.jsx';
import InputTypeahead from 'components/InputTypeahead';
import Layer from 'components/Layer.jsx';
import MenuButton from 'components/MenuButton.jsx';
import Radio from 'components/Radio.jsx';
import SearchExpanding from 'components/SearchExpanding';
import TextWithIcon from 'components/TextWithIcon.jsx';

import './ListActions.scss';

type ListActionsFilter = {
  inputProps?: Object,
  label?: any,
  name: string,
  onChange?: Function,
  options?: any,
  title: string,
  type: 'checkbox' | 'radio' | 'select',
};

export type ListActionsCheckboxFilterState = string[];
export type ListActionsRadioFilterState = string;
export type ListActionsSelectFilterState = { enabled: boolean, value: string };

export type ListActionsFilterState = {
  [string]:
    | ListActionsCheckboxFilterState
    | ListActionsRadioFilterState
    | ListActionsSelectFilterState,
};

type Props = {
  onFilterChange: Function,
  onQueryChange: Function,
  primaryFilters: Array<ListActionsFilter>,
  secondaryFilters: Array<ListActionsFilter>,
  filterButtonFace?: React.Element<any>,
  initialActiveFilters?: ListActionsFilterState,
};

type State = {
  activeFilters: ListActionsFilterState,
};

export default class ListActions extends React.Component<Props, State> {
  static defaultProps = {
    filterButtonFace: 'Filter',
  };

  state: State = {
    activeFilters: {},
  };

  componentWillMount() {
    const { initialActiveFilters } = this.props;
    this.setState({ activeFilters: initialActiveFilters || {} });
  }

  handleFilterChange = (value: *, name: string) => {
    const { onFilterChange } = this.props;

    this.setState(
      prevState => ({
        activeFilters: {
          ...prevState.activeFilters,
          [name]: value,
        },
      }),
      () => onFilterChange(this.state.activeFilters)
    );
  };

  handleQueryChange = (value: string) => {
    const { onQueryChange } = this.props;

    onQueryChange(value);
  };

  renderCheckbox = (name: string, inputProps: ?Object, options: Object) => {
    const { activeFilters } = this.state;
    const { [name]: filter } = activeFilters;
    return (
      <CheckboxList
        {...inputProps}
        defaultChecked={filter}
        options={options}
        onChange={e => this.handleFilterChange(e, name)}
        data-test="filter-checkbox-list"
      />
    );
  };

  renderRadioBox = (name: string, inputProps: ?Object, item: Object) => {
    const { activeFilters } = this.state;
    const { [name]: filter } = activeFilters;
    const { display, label, value } = item;

    return (
      <Radio
        {...inputProps}
        label={display || label}
        name={name}
        key={label}
        checked={filter === value}
        onChange={() => this.handleFilterChange(value, name)}
        data-test="filter-radio-input"
      />
    );
  };

  renderSelect = (name: string, inputProps: ?Object) => {
    const { activeFilters } = this.state;
    const { [name]: filter } = activeFilters;

    const selectFilter = ((filter: any): ListActionsSelectFilterState);

    return (
      <div className="ListActions-select">
        {this.renderSelectCheckbox(name, selectFilter)}
        <InputTypeahead
          {...inputProps}
          showDropdownButton
          disabled={(selectFilter && !selectFilter.enabled) || !selectFilter}
          selectedItem={selectFilter && selectFilter.value}
          onChange={value => this.handleFilterChange({ value, enabled: true }, name)}
          data-test="filter-select"
        />
      </div>
    );
  };

  renderSelectCheckbox = (name: string, value: ListActionsSelectFilterState) => (
    <Checkbox
      checked={!!(value && value.enabled)}
      onChange={() =>
        this.handleFilterChange(
          (() => {
            if (value) {
              return Object.assign({}, value, { enabled: !value.enabled });
            } else {
              return { enabled: true };
            }
          })(),
          name
        )
      }
    />
  );

  renderFilters = (filters: Array<ListActionsFilter>) =>
    filters.map(filter => {
      const { inputProps, name, options, title, type } = filter;

      return (
        <Field key={name}>
          <Heading level="3" levelStyle="5">
            {title}
          </Heading>
          {type === 'checkbox' && options && this.renderCheckbox(name, inputProps, options)}
          {type === 'radio' &&
            options &&
            options.map(item => this.renderRadioBox(name, inputProps, item))}
          {type === 'select' && this.renderSelect(name, inputProps)}
        </Field>
      );
    });

  render() {
    const { primaryFilters, secondaryFilters } = this.props;
    const { filterButtonFace } = this.props;

    const buttonProps = {
      color: 'polar-bear',
      level: 'secondary',
    };

    return (
      <SearchExpanding
        buttonProps={buttonProps}
        inputProps={{
          'data-test': 'search-input',
        }}
        className="ListActions"
        onCancel={() => this.handleQueryChange('')}
        onChange={this.handleQueryChange}>
        <MenuButton
          {...buttonProps}
          menu={
            <Layer className="ListActions-dropdown" data-test="filter-menu">
              <div className="ListActions-dropdown-section ListActions-dropdown-section--primary">
                {this.renderFilters(primaryFilters)}
              </div>
              <div className="ListActions-dropdown-section">
                {this.renderFilters(secondaryFilters)}
              </div>
            </Layer>
          }
          data-test="filter-button">
          <TextWithIcon
            appendIcon
            icon="filter"
            gutter="xxsmall"
            wrap={false}
            data-test="filter-button-text">
            {filterButtonFace}
          </TextWithIcon>
        </MenuButton>
      </SearchExpanding>
    );
  }
}
